import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { ThemeToggle } from "./ThemeToggle";
import { ROUTES } from "../config/routes";

const LINKS = [
  { label: "Product", href: "#features" },
  { label: "Manifesto", href: "#manifesto" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "#pricing" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="navbar"
        className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4"
      >
        <nav
          className={`flex w-full max-w-6xl items-center justify-between rounded-full border px-4 py-2.5 pl-5 transition-colors duration-300 ${
            scrolled
              ? "border-line bg-surface/80 shadow-soft backdrop-blur-xl"
              : "border-transparent bg-transparent"
          }`}
        >
          <a href="#top" data-testid="nav-logo" className="flex items-center" aria-label="BREELINK home">
            <BrandLogo eager className="h-6 sm:h-7" />
          </a>

          <div className="hidden items-center gap-8 md:flex">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="strike-link text-sm font-medium text-inkMuted hover:text-ink"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle testid="theme-toggle-desktop" className="hidden sm:grid" />
            <a
              href={ROUTES.login}
              data-testid="nav-login"
              className="strike-link hidden text-sm font-semibold text-ink md:inline-block"
            >
              Log in
            </a>
            <a
              href={ROUTES.register}
              data-testid="nav-cta"
              className="group hidden items-center gap-1.5 rounded-full bg-lime px-5 py-2.5 text-sm font-semibold text-limeText transition-transform hover:-translate-y-0.5 md:inline-flex"
            >
              Claim your link
              <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="grid h-10 w-10 place-items-center rounded-full border border-line bg-surfaceAlt text-ink md:hidden"
            >
              <Menu size={18} />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-bg px-6 py-6 md:hidden"
          >
            <div className="flex items-center justify-between">
              <BrandLogo eager className="h-6" />
              <button
                type="button"
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="grid h-10 w-10 place-items-center rounded-full border border-line bg-surfaceAlt text-ink"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-14 flex flex-1 flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.1 }}
                  className="font-display text-4xl font-semibold tracking-tight text-ink"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href={ROUTES.login}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-login"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * LINKS.length + 0.1 }}
                className="font-display text-4xl font-semibold tracking-tight text-ink"
              >
                Log in
              </motion.a>
            </div>

            <div className="flex items-center justify-between border-t border-line pt-6">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-inkMuted">Appearance</span>
                <ThemeToggle testid="theme-toggle-mobile" />
              </div>
              <a
                href={ROUTES.register}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta"
                className="inline-flex items-center gap-1.5 rounded-full bg-lime px-5 py-3 text-sm font-semibold text-limeText"
              >
                Claim your link <ArrowUpRight size={15} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
